import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const cartRoot = () => window.Shopify?.routes?.root || '/';

const parseJsonScript = (host, selector, fallback) => {
  const script = host.querySelector(selector);
  if (!script?.textContent) return fallback;

  try {
    return JSON.parse(script.textContent);
  } catch (error) {
    console.warn('[EVERIDE cart] Invalid JSON payload', error);
    return fallback;
  }
};

const getSectionIds = () => {
  const ids = new Set();

  document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      ids.add(component.dataset.sectionId);
    }
  });

  if (ids.size === 0) ids.add('cart-drawer-section');
  return Array.from(ids);
};

const requestJson = async (url, payload) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (!response.ok || data.status || data.errors) {
    const message = data.description || data.message || data.errors || 'Cart update failed';
    throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
  }

  return data;
};

const fetchCart = async () => {
  const response = await fetch(`${cartRoot()}cart.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error('Unable to refresh the cart');
  return response.json();
};

const dispatchCartError = (host, error) => {
  host.dispatchEvent(
    new CartErrorEvent({
      error: error?.message || 'Cart update failed',
      code: 'INVALID',
    })
  );
};

class EverideCartProgress extends HTMLElement {
  connectedCallback() {
    this.initializeFrame = requestAnimationFrame(() => this.update());
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.initializeFrame);
  }

  update() {
    const baseThresholdCents = Number(this.dataset.baseThresholdCents);
    const cartTotalCents = Number(this.dataset.cartTotalCents);
    const baseCurrency = this.dataset.baseCurrency || 'USD';
    const activeCurrency = this.dataset.activeCurrency || baseCurrency;
    const rawRate = Number(window.Shopify?.currency?.rate);
    const hasConversion = activeCurrency === baseCurrency || Number.isFinite(rawRate) && rawRate > 0;
    const rate = activeCurrency === baseCurrency ? 1 : rawRate;

    if (!Number.isFinite(baseThresholdCents) || baseThresholdCents <= 0) return;

    if (!hasConversion) {
      const baseLabel = this.formatMoney(baseThresholdCents, baseCurrency);
      this.style.setProperty('--everide-cart-progress', '0%');
      this.querySelector('[data-progress-message]')?.replaceChildren(`Free shipping over ${baseLabel}`);
      this.updateThresholdLabels(baseLabel);
      return;
    }

    const convertedThresholdCents = Math.max(1, Math.round(baseThresholdCents * rate));
    const remainingCents = Math.max(0, convertedThresholdCents - cartTotalCents);
    const unlocked = cartTotalCents >= convertedThresholdCents;
    const progress = Math.min(100, Math.max(0, cartTotalCents / convertedThresholdCents * 100));
    const thresholdLabel = this.formatMoney(convertedThresholdCents, activeCurrency);
    const message = this.querySelector('[data-progress-message]');

    this.style.setProperty('--everide-cart-progress', `${progress}%`);
    this.updateThresholdLabels(thresholdLabel);

    if (!message) return;

    if (unlocked) {
      const strong = document.createElement('strong');
      strong.textContent = 'Free shipping unlocked';
      message.replaceChildren(strong);
    } else {
      const strong = document.createElement('strong');
      strong.textContent = this.formatMoney(remainingCents, activeCurrency);
      message.replaceChildren("You're ", strong, ' away from free shipping');
    }
  }

  updateThresholdLabels(label) {
    document.querySelectorAll('[data-everide-shipping-threshold]').forEach((element) => {
      element.textContent = label;
    });
    document.querySelectorAll('[data-everide-shipping-threshold-plus]').forEach((element) => {
      element.textContent = `${label}+`;
    });
  }

  formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol',
      }).format(cents / 100);
    } catch (_error) {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  }
}

class EverideCartRecommendations extends HTMLElement {
  connectedCallback() {
    this.initializeFrame = requestAnimationFrame(() => this.initialize());
  }

  initialize() {
    if (this.dataset.carouselReady === 'true') return;

    this.rail = this.querySelector('[data-recommendations-rail]');
    this.previousButton = this.querySelector('[data-recommendations-previous]');
    this.nextButton = this.querySelector('[data-recommendations-next]');

    if (!this.rail || !this.previousButton || !this.nextButton) return;

    this.dataset.carouselReady = 'true';
    this.handleScroll = () => this.updateControls();
    this.handlePrevious = () => this.scrollByCard(-1);
    this.handleNext = () => this.scrollByCard(1);

    this.previousButton.addEventListener('click', this.handlePrevious);
    this.nextButton.addEventListener('click', this.handleNext);
    this.rail.addEventListener('scroll', this.handleScroll, { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.updateControls());
      this.resizeObserver.observe(this.rail);
    }

    requestAnimationFrame(() => this.updateControls());
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.initializeFrame);
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.rail?.removeEventListener('scroll', this.handleScroll);
    this.resizeObserver?.disconnect();
    delete this.dataset.carouselReady;
  }

  scrollByCard(direction) {
    const firstCard = this.rail.querySelector('.everide-cart-recommendation');
    if (!firstCard) return;

    const styles = getComputedStyle(this.rail);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 12;
    const distance = firstCard.getBoundingClientRect().width + gap;

    this.rail.scrollBy({ left: direction * distance, behavior: 'smooth' });
  }

  updateControls() {
    const maximumScroll = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
    const hasOverflow = maximumScroll > 4;
    const currentScroll = Math.abs(this.rail.scrollLeft);

    this.classList.toggle('is-static', !hasOverflow);
    this.previousButton.disabled = !hasOverflow || currentScroll <= 4;
    this.nextButton.disabled = !hasOverflow || currentScroll >= maximumScroll - 4;
  }
}

class EverideCartRecommendation extends HTMLElement {
  connectedCallback() {
    this.initializeFrame = requestAnimationFrame(() => this.initialize());
  }

  initialize() {
    if (this.dataset.recommendationReady === 'true') return;

    this.variants = parseJsonScript(this, '[data-recommendation-variants]', []);
    this.selects = Array.from(this.querySelectorAll('[data-recommendation-option]'));
    this.addButton = this.querySelector('[data-recommendation-add]');
    this.status = this.querySelector('[data-recommendation-status]');
    this.price = this.querySelector('[data-recommendation-price]');
    this.image = this.querySelector('.everide-cart-recommendation__image');

    if (!this.addButton || this.variants.length === 0) return;

    this.dataset.recommendationReady = 'true';
    this.handleOptionChange = () => this.updateSelectedVariant();
    this.handleAdd = () => this.addToCart();

    this.selects.forEach((select) => select.addEventListener('change', this.handleOptionChange));
    this.addButton.addEventListener('click', this.handleAdd);
    this.updateSelectedVariant();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.initializeFrame);
    this.selects?.forEach((select) => select.removeEventListener('change', this.handleOptionChange));
    this.addButton?.removeEventListener('click', this.handleAdd);
    delete this.dataset.recommendationReady;
  }

  updateSelectedVariant() {
    const selectedOptions = this.selects.map((select) => select.value);
    const exactVariant = this.variants.find((variant) =>
      variant.options.every((option, index) => option === selectedOptions[index])
    );
    this.selectedVariant = this.selects.length === 0 ? this.variants[0] : exactVariant;

    const isAvailable = Boolean(this.selectedVariant?.available);
    this.addButton.disabled = !isAvailable;

    if (this.price && this.selectedVariant?.price) {
      this.price.textContent = this.selectedVariant.price;
    }

    if (this.image && this.selectedVariant?.image) {
      this.image.removeAttribute('srcset');
      this.image.src = this.selectedVariant.image;
    }

    if (this.status) {
      this.status.textContent = isAvailable ? '' : 'This combination is unavailable';
    }
  }

  async addToCart() {
    if (!this.selectedVariant?.available || this.isAdding) return;

    this.isAdding = true;
    this.addButton.disabled = true;
    this.addButton.setAttribute('aria-busy', 'true');
    this.addButton.textContent = 'Adding…';
    if (this.status) this.status.textContent = '';

    const quantity = 1;
    const sectionIds = getSectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(this.selectedVariant.id), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await requestJson(`${cartRoot()}cart/add.js`, {
        items: [{ id: Number(this.selectedVariant.id), quantity }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      });
      const cart = await fetchCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: response.sections,
          items: cart.items,
          itemCount: quantity,
          productId: this.dataset.productId,
          source: 'everide-cart-recommendation',
          sourceId: this.dataset.productId,
          didError: false,
        },
      });

      this.addButton.textContent = 'Added';
    } catch (error) {
      deferred.reject(error);
      dispatchCartError(this, error);
      if (this.status) this.status.textContent = error.message;
      this.addButton.textContent = 'Add to cart';
      this.addButton.disabled = false;
      this.addButton.removeAttribute('aria-busy');
      this.isAdding = false;
    }
  }
}

class EverideCartLineVariant extends HTMLElement {
  connectedCallback() {
    this.initializeFrame = requestAnimationFrame(() => this.initialize());
  }

  initialize() {
    if (this.dataset.lineVariantReady === 'true') return;

    this.variants = parseJsonScript(this, '[data-line-variants]', []);
    this.properties = parseJsonScript(this, '[data-line-properties]', {});
    this.selects = Array.from(this.querySelectorAll('[data-line-option]'));
    this.status = this.querySelector('[data-line-variant-status]');
    this.currentVariantId = String(this.dataset.currentVariantId || '');

    if (this.variants.length === 0 || this.selects.length === 0) return;

    this.dataset.lineVariantReady = 'true';
    this.handleChange = () => this.changeVariant();
    this.selects.forEach((select) => select.addEventListener('change', this.handleChange));
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.initializeFrame);
    this.selects?.forEach((select) => select.removeEventListener('change', this.handleChange));
    delete this.dataset.lineVariantReady;
  }

  findSelectedVariant() {
    const selectedOptions = this.selects.map((select) => select.value);
    return this.variants.find((variant) =>
      variant.options.every((option, index) => option === selectedOptions[index])
    );
  }

  setDisabled(disabled) {
    this.selects.forEach((select) => {
      select.disabled = disabled;
    });
    this.toggleAttribute('aria-busy', disabled);
  }

  syncCurrentVariant() {
    const current = this.variants.find((variant) => String(variant.id) === this.currentVariantId);
    if (!current) return;

    this.selects.forEach((select, index) => {
      select.value = current.options[index];
    });
  }

  async changeVariant() {
    if (this.isUpdating) return;

    const selectedVariant = this.findSelectedVariant();

    if (!selectedVariant || !selectedVariant.available) {
      if (this.status) this.status.textContent = 'This combination is unavailable';
      return;
    }

    if (String(selectedVariant.id) === this.currentVariantId) {
      if (this.status) this.status.textContent = '';
      return;
    }

    this.isUpdating = true;
    this.setDisabled(true);
    if (this.status) this.status.textContent = 'Updating…';

    const quantity = Math.max(1, Number(this.dataset.quantity) || 1);
    const oldLineKey = this.dataset.lineKey;
    const sectionIds = getSectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [
          { id: oldLineKey, quantity: 0 },
          { merchandiseId: String(selectedVariant.id), quantity },
        ],
        promise: deferred.promise,
      })
    );

    try {
      const item = {
        id: Number(selectedVariant.id),
        quantity,
      };

      if (this.properties && !Array.isArray(this.properties) && Object.keys(this.properties).length > 0) {
        item.properties = this.properties;
      }

      if (this.dataset.sellingPlanId) {
        item.selling_plan = Number(this.dataset.sellingPlanId);
      }

      await requestJson(`${cartRoot()}cart/add.js`, { items: [item] });

      const cart = await requestJson(`${cartRoot()}cart/change.js`, {
        id: oldLineKey,
        quantity: 0,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      });

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: cart.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'everide-cart-line-variant',
          sourceId: oldLineKey,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      dispatchCartError(this, error);
      this.syncCurrentVariant();
      this.setDisabled(false);
      if (this.status) this.status.textContent = error.message;
      this.isUpdating = false;
    }
  }
}

if (!customElements.get('everide-cart-progress')) {
  customElements.define('everide-cart-progress', EverideCartProgress);
}
if (!customElements.get('everide-cart-recommendations')) {
  customElements.define('everide-cart-recommendations', EverideCartRecommendations);
}
if (!customElements.get('everide-cart-recommendation')) {
  customElements.define('everide-cart-recommendation', EverideCartRecommendation);
}
if (!customElements.get('everide-cart-line-variant')) {
  customElements.define('everide-cart-line-variant', EverideCartLineVariant);
}
